import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, catchError, map, of, retry } from "rxjs";
import {
	ContractViewModel,
	ContratoViewModel,
	ImageContrato,
	CentroAgrupadoContratoViewModel,
	AsociarCebeContratoRequest,
	AsociarCebeContratoResponse,
} from "../interfaces/contract.interface";
import { ContratoTimelineDto } from "../interfaces/contract-timeline.interface";

//models
import { environment } from "src/environments/environment";

//services
import {
	ApiEnum,
	HttpDataService,
} from "src/app/_core/providers/http-data.service";

@Injectable({
	providedIn: "root",
})
export class ContractService {
	constructor(
		private readonly httpDataService: HttpDataService,
		private readonly http: HttpClient,
	) {}

	/**
	 * Obtiene la lista de contratos
	 * @returns Observable con la lista de contratos
	 */
	getContractsList(): Observable<ContractViewModel[]> {
		return this.httpDataService
			.get$<
				ContractViewModel[]
			>(ApiEnum.API_ROOT, environment.endpoint.contratos.getContratosList)
			.pipe(
				map((contracts) => {
					return contracts;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<ContractViewModel[]>(
						"getContractsList",
					),
				),
			);
	}

	getContractsPadreList(): Observable<ContractViewModel[]> {
		return this.httpDataService
			.get$<
				ContractViewModel[]
			>(ApiEnum.API_ROOT, environment.endpoint.contratos.getContratosPadreList)
			.pipe(
				retry(3),
				catchError(
					this.httpDataService.handleError<ContractViewModel[]>(
						"getContractsPadreList",
					),
				),
			);
	}

	getCentrosByContratoPadre(
		id: number,
	): Observable<CentroAgrupadoContratoViewModel[]> {
		return this.httpDataService
			.get$<
				CentroAgrupadoContratoViewModel[]
			>(ApiEnum.API_ROOT, `${environment.endpoint.centros.getCentrosByContratoPadre}/${id}`)
			.pipe(
				retry(3),
				catchError(
					this.httpDataService.handleError<
						CentroAgrupadoContratoViewModel[]
					>("getCentrosByContratoPadre"),
				),
			);
	}

	/**
	 * Obtiene un contrato por su ID
	 * @param id ID del contrato
	 * @returns Observable con los datos del contrato
	 */
	getContractById(id: number): Observable<ContratoViewModel> {
		return this.httpDataService
			.get$<ContratoViewModel>(
				ApiEnum.API_ROOT,
				`${environment.endpoint.contratos.getContratoById}/${id}`,
			)
			.pipe(
				map((contract) => {
					return contract;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<ContratoViewModel>(
						"getContractById",
					),
				),
			);
	}

	/**
	 * Línea de vida del contrato (hitos + eventos satélite). Autorizada por la
	 * visibilidad del contrato (404 fuera de alcance) — sin retry: si no hay
	 * acceso, insistir no ayuda. El caller trata null/undefined como "ocultar".
	 */
	getContractTimeline(id: number): Observable<ContratoTimelineDto> {
		return this.httpDataService
			.get$<ContratoTimelineDto>(
				ApiEnum.API_ROOT,
				`/Contratos/Timeline/${id}`,
			)
			.pipe(
				catchError(
					this.httpDataService.handleError<ContratoTimelineDto>(
						"getContractTimeline",
					),
				),
			);
	}

	/**
	 * Obtiene la imagen de un contrato por su ID
	 * @param id ID del contrato
	 * @returns Observable con los datos de la imagen
	 */
	getContractImage(id: number): Observable<ImageContrato> {
		return this.httpDataService
			.get$<ImageContrato>(
				ApiEnum.API_ROOT,
				`${environment.endpoint.contratos.getContratoImage}/${id}`,
			)
			.pipe(
				map((image) => {
					return image;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<ImageContrato>(
						"getContractImage",
					),
				),
			);
	}

	/**
	 * Crea o edita un contrato
	 * @param contract Datos del contrato a crear o editar
	 * @returns Observable con el contrato creado/editado
	 */
	saveContract(contract: ContratoViewModel): Observable<ContratoViewModel> {
		return this.httpDataService
			.put$<ContratoViewModel>(
				ApiEnum.API_ROOT,
				environment.endpoint.contratos.updateContrato,
				contract,
			)
			.pipe(
				map((updatedContract) => {
					return updatedContract;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<ContratoViewModel>(
						"saveContract",
					),
				),
			);
	}

	// Legacy methods for backward compatibility
	/**
	 * Crea un nuevo contrato
	 * @param contract Datos del contrato a crear
	 * @returns Observable con el contrato creado
	 */
	createContract(contract: ContratoViewModel): Observable<ContratoViewModel> {
		return this.httpDataService
			.post$<ContratoViewModel>(
				ApiEnum.API_ROOT,
				environment.endpoint.contratos.createContrato,
				contract,
			)
			.pipe(
				map((createdContract) => {
					return createdContract;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<ContratoViewModel>(
						"createContract",
					),
				),
			);
	}

	/**
	 * Edita un contrato existente
	 * @param contract Datos del contrato a editar
	 * @returns Observable con el contrato editado
	 */
	editContract(contract: ContratoViewModel): Observable<ContratoViewModel> {
		return this.httpDataService
			.put$<ContratoViewModel>(
				ApiEnum.API_ROOT,
				environment.endpoint.contratos.updateContrato,
				contract,
			)
			.pipe(
				map((updatedContract) => {
					return updatedContract;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<ContratoViewModel>(
						"editContract",
					),
				),
			);
	}

	// Old legacy methods for backward compatibility
	/**
	 * Crea un nuevo contrato (legacy)
	 * @param contract Datos del contrato a crear
	 * @returns Observable con el contrato creado
	 * @deprecated Use createContract() instead
	 */
	createContractLegacy(
		contract: ContractViewModel,
	): Observable<ContractViewModel> {
		// Using legacy HttpClient for backward compatibility
		return this.http.post<ContractViewModel>("/api/contracts", contract);
	}

	/**
	 * Crea un contrato desde una oportunidad
	 * @param opportunityId ID de la oportunidad
	 * @param contract Datos del contrato a crear
	 * @returns Observable con el contrato creado
	 */
	createContractFromOpportunity(
		opportunityId: number,
		contract: ContractViewModel,
	): Observable<ContractViewModel> {
		// Using legacy HttpClient for backward compatibility
		return this.http.post<ContractViewModel>(
			`/api/contracts/from-opportunity/${opportunityId}`,
			contract,
		);
	}

	/**
	 * Actualiza un contrato existente
	 * @param id ID del contrato
	 * @param contract Datos del contrato a actualizar
	 * @returns Observable con el contrato actualizado
	 */
	updateContract(
		id: number,
		contract: ContractViewModel,
	): Observable<ContractViewModel> {
		// Using legacy HttpClient for backward compatibility
		return this.http.put<ContractViewModel>(
			`/api/contracts/${id}`,
			contract,
		);
	}

	/**
	 * Elimina un contrato
	 * @param id ID del contrato
	 * @returns Observable con la respuesta de eliminación
	 */
	deleteContract(id: number): Observable<void> {
		// Using legacy HttpClient for backward compatibility
		return this.http.delete<void>(`/api/contracts/${id}`);
	}

	/**
	 * Subir datos financieros
	 * @param data Datos financieros a subir
	 * @returns Observable con la respuesta
	 */
	uploadFinancialData(data: any): Observable<any> {
		return this.httpDataService
			.post$<any>(
				ApiEnum.API_ROOT,
				environment.endpoint.contratos.uploadFinancialData,
				data,
			)
			.pipe(
				map((response) => {
					return response;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<any>(
						"uploadFinancialData",
					),
				),
			);
	}

	/**
	 * Notificación de cese
	 * @param data Datos de notificación
	 * @returns Observable con la respuesta
	 */
	notificacionCese(data: any): Observable<any> {
		return this.httpDataService
			.post$<any>(
				ApiEnum.API_ROOT,
				environment.endpoint.contratos.notificacionCese,
				data,
			)
			.pipe(
				map((response) => {
					return response;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<any>("notificacionCese"),
				),
			);
	}

	/**
	 * Notificación de alta
	 * @param data Datos de notificación
	 * @returns Observable con la respuesta
	 */
	notificacionAlta(data: any): Observable<any> {
		return this.httpDataService
			.post$<any>(
				ApiEnum.API_ROOT,
				environment.endpoint.contratos.notificacionAlta,
				data,
			)
			.pipe(
				map((response) => {
					return response;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<any>("notificacionAlta"),
				),
			);
	}

	associateCebeToContract(
		payload: AsociarCebeContratoRequest,
	): Observable<AsociarCebeContratoResponse> {
		return this.httpDataService
			.post$<AsociarCebeContratoResponse>(
				ApiEnum.API_ROOT,
				environment.endpoint.contratos.asociarCebeContrato,
				payload,
			)
			.pipe(
				catchError(
					this.httpDataService.handleError<AsociarCebeContratoResponse>(
						"associateCebeToContract",
					),
				),
			);
	}
}
